import {
  BookOpen, ChevronLeft, ChevronRight, Copy, Lock, RotateCw, Share, Wifi,
} from 'lucide-react';
import { FC, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { IPhoneMockup } from 'react-device-mockup';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';

import {
  BatteryPercent, BottomBar, Browser, BrowserNav, Empty, Footer, FooterText, Frame, QrBox, ReloadButton,
  ScanHint, ScreenClip, Stage, StageInner, StatusBar, StatusIcons, StatusTime,
  UrlPill,
  Version, Wrapper,
} from './GuestAppPreview.styled';

/**
 * Szerokość ekranu iPhone'a 15/16 w punktach CSS. Makietę rysujemy ZAWSZE w tej
 * szerokości, a do szuflady dopasowujemy ją skalowaniem — inaczej aplikacja
 * gościa dostawałaby węższy viewport i układ nie odpowiadałby temu, co widzi gość.
 */
const DEVICE_SCREEN_WIDTH = 390;
/** Tak samo jak biblioteka liczy wysokość paska stanu dla wariantu z wyspą. */
const STATUS_INSET = 59;
/** Wcięcie boczne paska stanu — mniej więcej tam, gdzie iOS stawia godzinę. */
const STATUS_PAD = 22;
/** Bok kodu QR — tyle wystarczy telefonowi, a nie zabiera miejsca telefonowi na ekranie. */
const QR_SIZE = 56;
/** Odstęp między telefonem a stopką — musi zgadzać się z `gap` we `Wrapper`. */
const STAGE_GAP = 26;
/**
 * Przybliżony rozmiar makiety w naturalnej skali (ekran 390 px + obudowa).
 * Służy WYŁĄCZNIE do pierwszego renderu, zanim zmierzymy prawdziwy element —
 * dzięki temu podgląd nigdy nie zostaje pusty, nawet gdy pomiar zawiedzie.
 */
const APPROX_DEVICE = { width: 416, height: 900 };
/** Poniżej tej skali podgląd przestaje być czytelny — lepiej przyciąć niż zwijać do znaczka. */
const MIN_SCALE = 0.25;

interface DeviceFit {
  scale: number;
  width: number;
  height: number;
}

const fitFor = (available: number, naturalHeight: number, naturalWidth: number): DeviceFit => {
  const scale = Math.max(MIN_SCALE, Math.min(1, available / naturalHeight));
  return {
    scale,
    width: Math.floor(naturalWidth * scale),
    height: Math.floor(naturalHeight * scale),
  };
};

/** Wysokość, jaką szuflada zwykle zostawia telefonowi — nagłówek, stopka i marginesy. */
const APPROX_CHROME = 240;

/**
 * Godzina na makiecie jest STAŁA. Prawdziwy zegar niczego tu nie wnosił — to
 * atrapa telefonu, a nie zegarek — za to kosztował timer i przerysowanie przy
 * każdym tiku.
 */
const STATUS_TIME = '21:37';

/** Stan baterii na makiecie — poniżej 20% iOS koloruje ją na czerwono. */
const BATTERY_PERCENT = 9;

/**
 * Zasięg jak w iOS: cztery słupki rosnącej wysokości, wygaszone poza tymi,
 * które „łapią". Lucide daje w tym miejscu wykres słupkowy o innych proporcjach,
 * więc rysujemy własny — pasek stanu to detal, po którym poznaje się telefon.
 */
const SignalBars: FC<{ active?: number }> = ({ active = 1 }) => (
  <svg width="17" height="12" viewBox="0 0 17 12" aria-hidden="true">
    {[0, 1, 2, 3].map((index) => {
      const height = 3.5 + index * 2.5;
      return (
        <rect
          key={index}
          x={index * 4.5}
          y={11 - height}
          width="3"
          height={height}
          rx="1"
          fill="currentColor"
          opacity={index < active ? 1 : 0.28}
        />
      );
    })}
  </svg>
);

/** Bateria iOS: obrys, kikut na styku i wypełnienie proporcjonalne do stanu. */
const BatteryIcon: FC<{ percent: number }> = ({ percent }) => {
  const inner = 21 * (Math.max(0, Math.min(100, percent)) / 100);

  return (
    <svg width="27" height="13" viewBox="0 0 27 13" aria-hidden="true">
      <rect
        x="0.5"
        y="0.5"
        width="24"
        height="12"
        rx="3.5"
        fill="none"
        stroke="currentColor"
        strokeOpacity="0.35"
      />
      <path d="M26 4.5v4a2 2 0 0 0 0-4z" fill="currentColor" opacity="0.35" />
      <rect x="2" y="2" width={inner} height="9" rx="2" fill={percent <= 20 ? '#ff3b30' : 'currentColor'} />
    </svg>
  );
};

interface GuestAppPreviewProps {
  /** Adres aplikacji gościa obiektu — `organizations/{id}.webAppUrl`. */
  url: string;
}

/**
 * Podgląd aplikacji gościa w obudowie telefonu.
 *
 * UWAGA NA CYKL ŻYCIA: ten komponent montuje pełną, drugą aplikację w karcie —
 * własny Firebase, własne nasłuchy Firestore (czyli PŁATNE odczyty tak długo, jak
 * ramka żyje) i własne zdjęcia. Dlatego rodzic ma go renderować warunkowo przy
 * otwarciu szuflady i pozwolić mu się odmontować przy zamknięciu. Chowanie przez
 * `display: none` zostawiłoby otwarty websocket i nasłuchy w tle.
 *
 * Ramka celowo NIE ma atrybutu `sandbox`: guest hub loguje gościa przez
 * `signInWithPopup`, a sandbox bez `allow-popups` zabiłby to logowanie.
 */
export const GuestAppPreview: FC<GuestAppPreviewProps> = ({ url }) => {
  const { t } = useTranslation();
  const hostRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const deviceRef = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState<DeviceFit>(() => fitFor(
    window.innerHeight - APPROX_CHROME,
    APPROX_DEVICE.height,
    APPROX_DEVICE.width,
  ));
  /** Zmiana klucza przemontowuje ramkę — z innej domeny nie wywołamy jej `reload()`. */
  const [reloadKey, setReloadKey] = useState(0);
  const [version, setVersion] = useState<string | null>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);

  /**
   * Zabezpieczenie na wypadek, gdyby cokolwiek przytrzymało węzeł ramki dłużej
   * niż React (animacja wyjścia szuflady, referencja z zewnątrz). Pusty adres
   * ubija aplikację gościa natychmiast: zamyka websocket do Firestore i kasuje
   * jej nasłuchy, zamiast czekać na sprzątanie przez przeglądarkę.
   */
  useEffect(() => () => {
    if (frameRef.current) frameRef.current.src = 'about:blank';
  }, []);

  /**
   * Dopasowanie makiety do szuflady. Mierzymy `offsetWidth/Height`, bo te wymiary
   * są SPRZED transformacji — dają naturalny rozmiar telefonu niezależnie od
   * aktualnej skali, więc pomiar się nie zapętla.
   */
  useLayoutEffect(() => {
    const measure = () => {
      const host = hostRef.current;
      const device = deviceRef.current;
      if (!host || !device) return;

      // Wymiary sprzed transformacji — dzielimy przez aktualną skalę, bo mierzony
      // element już ją ma na sobie, a `offsetWidth` liczy się w jego układzie.
      const naturalWidth = device.offsetWidth;
      const naturalHeight = device.offsetHeight;
      const availableHeight = host.clientHeight - (footerRef.current?.offsetHeight ?? 0) - STAGE_GAP;

      // Szuflada jeszcze się wysuwa albo pomiar wypadł pusty — zostawiamy oszacowanie.
      if (naturalWidth < 100 || naturalHeight < 100 || availableHeight < 100) return;

      const next = fitFor(availableHeight, naturalHeight, naturalWidth);
      // Bez tego każde zgłoszenie obserwatora tworzyłoby nowy obiekt stanu i
      // przerysowywało podgląd, nawet gdy wymiary wyszły identyczne.
      setFit((prev) => (
        prev.scale === next.scale && prev.width === next.width && prev.height === next.height
          ? prev
          : next
      ));
    };

    measure();
    const observer = new ResizeObserver(measure);
    if (hostRef.current) observer.observe(hostRef.current);
    return () => observer.disconnect();
  }, []);

  /**
   * Aplikacja gościa w trybie podglądu sama zgłasza swój build. Inaczej byłby
   * nieosiągalny: to inna domena, więc panel nie zajrzy jej do środka.
   */
  useEffect(() => {
    let origin = '';
    try {
      origin = new URL(url).origin;
    } catch {
      return undefined;
    }

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== origin) return;
      const data = event.data as { type?: string; version?: string } | null;
      if (data?.type === 'hs-preview-version' && typeof data.version === 'string') {
        setVersion(data.version);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [url]);

  /**
   * `?preview=1` mówi aplikacji gościa, że siedzi w ramce podglądu — po tym
   * znaczniku chowa paski przewijania, żeby obudowa telefonu nie zdradzała okna
   * przeglądarki.
   */
  const previewUrl = useMemo(() => {
    try {
      const parsed = new URL(url);
      parsed.searchParams.set('preview', '1');
      return parsed.toString();
    } catch {
      // Adres z bazy bywa niepełny — wtedy lepiej pokazać go bez znacznika niż nic.
      return url;
    }
  }, [url]);

  /** Sam host w pasku adresu — pełny link z parametrami i tak by się nie zmieścił. */
  const hostname = useMemo(() => {
    try {
      return new URL(url).hostname;
    } catch {
      return url;
    }
  }, [url]);

  if (!url) {
    return (
      <Empty>
        {t('guestAppPreview.missingUrl', 'Ten obiekt nie ma jeszcze ustawionego adresu aplikacji gościa.')}
      </Empty>
    );
  }

  return (
    <Wrapper ref={hostRef}>
      {/* Pasek stanu biblioteki jest wyłączony: dawał sam kolor tła i wyspę, bez
          godziny i wskaźników. Rysujemy własny, a wyspa i tak ląduje na nim. */}
      <Stage $width={fit.width} $height={fit.height}>
        <StageInner ref={deviceRef} $scale={fit.scale}>
          <IPhoneMockup screenWidth={DEVICE_SCREEN_WIDTH} hideStatusBar frameColor="#1f2937">
            <Browser>
              {/* Wyspę aparatu rysuje biblioteka; my dokładamy tylko godzinę i wskaźniki. */}
              <StatusBar aria-hidden="true" $inset={STATUS_INSET} $pad={STATUS_PAD}>
                <StatusTime>{STATUS_TIME}</StatusTime>
                <StatusIcons>
                  <SignalBars active={1} />
                  <Wifi />
                  <BatteryPercent>{`${BATTERY_PERCENT}%`}</BatteryPercent>
                  <BatteryIcon percent={BATTERY_PERCENT} />
                </StatusIcons>
              </StatusBar>

              <ScreenClip>
                <Frame
                  key={reloadKey}
                  src={previewUrl}
                  title={t('topbar.guestApp', 'Hotelspot Guest App preview')}
                  allow="camera; microphone; geolocation; payment; clipboard-write"
                />
              </ScreenClip>

              {/* Atrapa przeglądarki na dole — tak jak Safari na iOS. Działa tylko
              odświeżanie, reszta to grafika. */}
              <BottomBar>
                <UrlPill>
                  <Lock />
                  <span>{hostname}</span>
                  <ReloadButton
                    type="button"
                    aria-label={t('guestAppPreview.reload', 'Odśwież')}
                    onClick={() => setReloadKey((key) => key + 1)}
                  >
                    <RotateCw />
                  </ReloadButton>
                </UrlPill>

                <BrowserNav aria-hidden="true">
                  <ChevronLeft />
                  <ChevronRight />
                  <Share />
                  <BookOpen />
                  <Copy />
                </BrowserNav>
              </BottomBar>
            </Browser>
          </IPhoneMockup>
        </StageInner>
      </Stage>

      <Footer ref={footerRef}>
        {/* QR prowadzi pod CZYSTY adres, bez `?preview=1` — gość ma dostać
            normalną aplikację, nie tryb podglądu. */}
        <QrBox>
          <QRCode value={url} size={QR_SIZE} />
        </QrBox>
        <FooterText>
          <ScanHint>{t('guestAppPreview.scanHint', 'Zeskanuj, aby otworzyć na telefonie')}</ScanHint>
          <Version>
            {version
              ? t('guestAppPreview.version', 'Wersja {{version}}', { version })
              : t('guestAppPreview.versionUnknown', 'hotelspot guest app')}
          </Version>
        </FooterText>
      </Footer>
    </Wrapper>
  );
};
